import React, { useState, useRef, useEffect } from 'react';
import { X, Send, User } from 'lucide-react';
import { getProducts, Product } from '../services/productService';
import { logger } from '../utils/logger';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hello! I'm here to help you with Simpl Processing. I can answer questions about our payment processing solutions, POS systems, products, pricing, and more. How can I assist you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load products when component mounts
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productsData = await getProducts();
        setProducts(productsData);
      } catch (error) {
        logger.error('Error loading products for chatbot:', error);
      }
    };
    loadProducts();

    // Check available models on mount
    const checkAvailableModels = async () => {
      try {
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY || 'AIzaSyBMtC6rwqhHPqsLPhSRbeAWzRXx4UWqLzo';
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
        );
        if (response.ok) {
          const data = await response.json();
          logger.log('Available Gemini models:', data.models?.map((m: any) => m.name) || []);
        }
      } catch (error) {
        logger.error('Error checking available models:', error);
      }
    };
    checkAvailableModels();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY || 'AIzaSyBMtC6rwqhHPqsLPhSRbeAWzRXx4UWqLzo';
      
      if (!apiKey || apiKey === 'your_gemini_api_key_here') {
        throw new Error('Gemini API key not configured');
      }
      
      // Build product information string
      let productsInfo = '';
      if (products.length > 0) {
        productsInfo = '\n\nCurrent Product Catalog:\n';
        products.forEach((product, index) => {
          productsInfo += `${index + 1}. ${product.name}`;
          if (product.category) {
            const categoryMap: Record<string, string> = {
              'pos-systems': 'POS Systems',
              'mobile-solutions': 'Mobile Solutions',
              'online-payments': 'Online Payments',
              'handheld-terminals': 'Handheld Terminals'
            };
            productsInfo += ` (${categoryMap[product.category] || product.category})`;
          }
          if (product.price) productsInfo += ` - Price: ${product.price}`;
          if (product.description) productsInfo += `\n   Description: ${product.description}`;
          if (product.features && product.features.length > 0) {
            productsInfo += `\n   Features: ${product.features.join(', ')}`;
          }
          if (product.industry) {
            const industries = Array.isArray(product.industry) ? product.industry : [product.industry];
            productsInfo += `\n   Industries: ${industries.join(', ')}`;
          }
          productsInfo += '\n';
        });
      } else {
        productsInfo = '\n\nNote: Product catalog is being loaded. You can still help with general questions about POS systems, payment processing, and services.';
      }
      
      // System context for the chatbot - Comprehensive knowledge base
      const systemContext = `You are a helpful assistant for Simpl Processing, a leading payment processing company. 

COMPANY INFORMATION:
- Company Name: Simpl Processing
- Tagline: "Make your payments Simpl."
- Location: New York, NY
- Business Hours: Mon-Fri 9AM-6PM EST
- Phone: (201) 846-0591
- Email: simran@simplprocessing.com
- Website: simplprocessing.com

KEY FEATURES & BENEFITS:
1. Same-Day Deposits: Get your money faster with same-day deposits, 7 days a week at no extra cost
2. Secure & Reliable: Bank-level security with 99.9% uptime guarantee for uninterrupted business operations
3. 24/7 Support: Round-the-clock customer support to help you whenever you need assistance
4. Bank-Level Security: PCI DSS compliant with end-to-end encryption
5. Always Connected: WiFi, 5G, and offline modes for reliability
6. Competitive Rates: Transparent pricing with no hidden fees

SERVICES OFFERED:
- Payment Processing Solutions: Professional POS solutions with same-day deposits, competitive rates, and 24/7 support
- POS Systems: Complete point-of-sale solutions including hardware, software, and payment processing
- Mobile Solutions: Mobile payment processing for on-the-go businesses and pop-up shops
- Online Payments: Secure online payment processing for e-commerce and digital transactions
- Handheld Terminals: Portable payment terminals for mobile businesses

PRODUCT CATEGORIES:
1. POS Systems: Complete point-of-sale solutions including hardware, software, and payment processing
2. Mobile Solutions: Mobile payment processing for on-the-go businesses and pop-up shops
3. Online Payments: Secure online payment processing for e-commerce and digital transactions
4. Handheld Terminals: Portable payment terminals for mobile businesses

COMPANY STATISTICS:
- 10+ Years Experience: Serving businesses with reliable payment solutions
- 500+ Happy Customers: Businesses trust us with their payment processing
- $1B+ Processed: In transaction volume processed annually

WHAT WE SELL:
- POS Systems: Complete point-of-sale solutions including hardware, software, and payment processing
- Online Payments: Secure online payment processing for e-commerce and digital transactions
- Mobile Solutions: Mobile payment processing for on-the-go businesses and pop-up shops

WEBSITE PAGES:
- Home (/): Main landing page with company overview, features, testimonials, and contact information
- Products (/products): Full product catalog where customers can browse, compare, and request quotes
- Contact (/contact): Contact form for inquiries and quote requests
- Customer Login: Portal for existing customers to access their dashboard

${productsInfo}

CUSTOMER TESTIMONIALS:
- Sarah Johnson (Johnson's Boutique): "Simpl Processing has transformed our payment experience. Same-day deposits and excellent support!"
- Mike Wilson (Wilson's Auto Repair): "The POS system is incredibly easy to use and the fees are very competitive. Highly recommended!"
- Emily Davis (Davis Beauty Salon): "Customer service is outstanding and the integration was seamless. Our business has grown significantly."

HOW TO HELP CUSTOMERS:
1. Product Questions:
   - Reference specific products by name from the catalog if they match what the customer is looking for
   - Mention product categories, features, and specifications
   - If a product matches their industry, highlight that
   - Suggest they visit /products page to see full catalog with images and detailed specs
   - Offer to help them get a quote if they're interested

2. Pricing Questions:
   - Mention that pricing is competitive and transparent with no hidden fees
   - Explain that rates may vary based on business type and volume
   - Direct them to request a quote for specific pricing
   - Mention same-day deposits are available at no extra cost

3. General Questions:
   - Be friendly, professional, and helpful
   - Use the company information above to answer questions accurately
   - If you don't know something specific, direct them to contact the team
   - Always provide contact information: (201) 846-0591 or simran@simplprocessing.com

4. Getting Started:
   - Suggest they visit /contact to request a quote
   - Mention they can also call (201) 846-0591 during business hours
   - Explain the process: they'll get a quote, then can set up their account

IMPORTANT: Always be helpful, professional, and accurate. If you're unsure about specific pricing or technical details, direct customers to contact the team directly. Never make up pricing or product specifications that aren't in the product catalog.`;

      // Use correct model names from available models list
      // Available models include: gemini-2.5-flash, gemini-2.5-pro, gemini-flash-latest, gemini-pro-latest
      let modelName = 'gemini-2.5-flash';
      
      logger.log('Calling Gemini API with model:', modelName, 'API Key:', apiKey.substring(0, 10) + '...');
      
      const makeApiCall = async (model: string, apiVersion: string = 'v1beta') => {
        // Try v1beta API (standard endpoint)
        const url = `https://generativelanguage.googleapis.com/${apiVersion}/models/${model}:generateContent?key=${apiKey}`;
        logger.log(`Attempting API call to ${apiVersion}:`, url.replace(apiKey, 'API_KEY_HIDDEN'));
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: `${systemContext}\n\nConversation history:\n${messages.map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n')}\n\nUser: ${userMessage}\nAssistant:`
                  }
                ]
              }
            ],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 1024,
            },
          }),
        });
        return { response, apiVersion };
      };

      // Try v1beta first (standard), then v1 as fallback
      let result = await makeApiCall(modelName, 'v1beta');
      let response = result.response;

      // If 404, try different approaches
      if (!response.ok && response.status === 404) {
        // First try v1 API version
        if (result.apiVersion === 'v1beta') {
          logger.log('Trying v1 API version...');
          result = await makeApiCall(modelName, 'v1');
          response = result.response;
        }
        
        // If still 404, try other available models from the list
        if (!response.ok && response.status === 404) {
          // Use models that are confirmed available from ListModels
          const fallbackModels = [
            'gemini-2.5-pro',
            'gemini-2.0-flash',
            'gemini-2.0-flash-001',
            'gemini-flash-latest',
            'gemini-pro-latest'
          ];
          for (const fallbackModel of fallbackModels) {
            if (modelName !== fallbackModel) {
              logger.log(`Trying fallback model: ${fallbackModel}`);
              modelName = fallbackModel;
              result = await makeApiCall(modelName, 'v1beta');
              response = result.response;
              if (response.ok) {
                logger.log(`✅ Success with model: ${fallbackModel}`);
                break;
              }
            }
          }
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Gemini API Error:', response.status, errorData);
        logger.error('Full error details:', JSON.stringify(errorData, null, 2));
        
        // Log the full error for debugging
        if (errorData.error) {
          logger.error('Error message:', errorData.error.message);
          logger.error('Error status:', errorData.error.status);
        }
        
        // Provide helpful error messages
        if (response.status === 404) {
          const errorMsg = errorData.error?.message || 'Model not found';
          throw new Error(`API endpoint not found (404). ${errorMsg}. Please check: 1) API key is correct, 2) Generative Language API is enabled, 3) API key restrictions allow this API, 4) Model name is correct`);
        } else if (response.status === 403) {
          throw new Error('API access denied. Check API key restrictions and ensure Generative Language API is enabled.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request: ${JSON.stringify(errorData)}`);
        }
        
        throw new Error(`API Error: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      logger.log('✅ Gemini API Success! Response received');
      const assistantMessage = data.candidates?.[0]?.content?.parts?.[0]?.text || 
        "I apologize, but I'm having trouble processing that right now. Please contact our team at (201) 846-0591 or simran@simplprocessing.com for assistance.";

      logger.log('Assistant message generated:', assistantMessage.substring(0, 100) + '...');
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
    } catch (error) {
      logger.error('Chatbot error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I'm experiencing technical difficulties. Please contact our team directly at (201) 846-0591 or email simran@simplprocessing.com for immediate assistance."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-3 min-h-[48px] min-w-[48px] rounded-full shadow-lg hover:from-gray-900 hover:to-black transition-all duration-300 z-50 flex items-center justify-center group touch-manipulation"
          aria-label="Open chatbot"
        >
          <img 
            src="/logo-simpl-payments.png" 
            alt="Simpl Processing" 
            className="h-8 w-8 rounded-full group-hover:scale-110 transition-transform object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src !== `${window.location.origin}/logo.png`) {
                target.src = '/logo.png';
              }
            }}
          />
          <span className="absolute -top-1 -right-1 bg-green-500 w-3 h-3 rounded-full animate-pulse"></span>
        </button>
      )}

      {/* Chat Window - full screen on mobile, panel on desktop */}
      {isOpen && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-96 sm:h-[600px] sm:max-h-[85vh] sm:rounded-lg bg-white shadow-2xl flex flex-col z-50 border-0 sm:border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 sm:rounded-t-lg rounded-t-none flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-white/20 p-1.5 rounded-lg flex-shrink-0">
                <img 
                  src="/logo-simpl-payments.png" 
                  alt="Simpl Processing Logo" 
                  className="h-6 w-auto rounded object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.src !== `${window.location.origin}/logo.png`) {
                      target.src = '/logo.png';
                    }
                  }}
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-sm sm:text-base truncate">Simpl Processing Assistant</h3>
                <p className="text-xs text-gray-300">We're here to help!</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-300 transition-colors p-2.5 -m-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
              aria-label="Close chatbot"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="bg-white p-1 rounded-lg flex-shrink-0 h-8 w-8 flex items-center justify-center border border-gray-200 shadow-sm">
                    <img 
                      src="/logo.png" 
                      alt="Simpl Processing" 
                      className="h-full w-full object-contain rounded"
                    />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="bg-gray-800 text-white p-2 rounded-full flex-shrink-0 h-8 w-8 flex items-center justify-center">
                    <User className="h-4 w-4" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="bg-white p-1 rounded-lg flex-shrink-0 h-8 w-8 flex items-center justify-center border border-gray-200 shadow-sm">
                  <img 
                    src="/logo.png" 
                    alt="Simpl Processing" 
                    className="h-full w-full object-contain rounded"
                  />
                </div>
                <div className="bg-white text-gray-900 border border-gray-200 rounded-lg p-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - touch-friendly on mobile */}
          <div className="border-t border-gray-200 p-3 sm:p-4 bg-white flex-shrink-0 safe-area-pb">
            <div className="flex gap-2 items-center">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-3 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent text-base touch-manipulation"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="bg-gradient-to-r from-gray-800 to-gray-900 text-white min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 p-2.5 sm:p-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center touch-manipulation"
                aria-label="Send message"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Powered by Gemini AI
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;

